<script setup lang="ts">
/**
 * UI-kit component
 * Category: Fields
 * Name: MultiSelectSearchable
 */
import { vOnClickOutside } from '@vueuse/components'
import VueSelect from 'vue-select'

import type { MultiselectSearchOption, MultiSelectSearchableProps } from '../types/interfaces'

import { faSquare } from '@fortawesome/pro-regular-svg-icons'
import { faXmark, faSquareCheck } from '@fortawesome/pro-solid-svg-icons'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { AppText } from '@shared/ui/typography'

import { useMultiSelectSearchable } from '../composables/use-multi-select-searchable.hook'

defineOptions({
  name: 'MultiSelectSearchable',
})

const props = withDefaults(defineProps<MultiSelectSearchableProps>(), {
  value: () => ([]),
  placeholder: '',
  label: '',
  disabled: false,
  isAllAvailable: false,
  required: false,
  mode: 'normal',
  reduceKey: 'text',
  isAvailablePosition: false,
  dataQa: null,
  optionalLabel: '',
})

const emit = defineEmits<{
  'select:before': [MultiselectSearchOption]
  'deselect:before': [MultiselectSearchOption]
  'selected': [MultiselectSearchOption[]]
  'validation': [boolean]
  'dropdown:close': []
}>()

const isChecked = (selected: boolean) => selected ? faSquareCheck : faSquare

const {
  refUpdateCount,
  refAllSelected,
  refOpen,
  refValue,
  refOptions,
  isDisabled,
  placeholderClass,
  inputText,
  dropdownStyle,
  handleBeforeSelect,
  handleBeforeDeselect,
  handleSelected,
  handleDeselected,
  handleFocus,
  handleBlur,
  handleSearch,
  handleClickOutside,
  handleClickClose,
  handleOpen,
  setToggleAll,
} = useMultiSelectSearchable({
  props,
  onSelectBefore: (option: MultiselectSearchOption) => emit('select:before', option),
  onDeselectBefore: (option: MultiselectSearchOption) => emit('deselect:before', option),
  onSelected: (options: MultiselectSearchOption[]) => emit('selected', options),
  onValidation: (valid: boolean) => emit('validation', valid),
  onDropdownClose: () => emit('dropdown:close'),
})
</script>

<template>
  <div
    ref="selectSearch"
    :class="['input-select', { 'is-available-position': isAvailablePosition }]"
    :data-qa="dataQa"
    :style="dropdownStyle"
  >
    <div class="input-select__label-box">
      <AppText
        v-if="label"
        name="body2"
        :class="['input-select__label', { 'disabled': isDisabled }]"
      >
        {{ label }}
        <span
          v-if="required"
          class="input-select__required"
        >*</span>
      </AppText>
      <AppText
        v-if="optionalLabel"
        name="body2"
        :class="['input-select__optional', { 'disabled': isDisabled }]"
      >
        {{ optionalLabel }}
      </AppText>
    </div>
    <VueSelect
      :key="refUpdateCount"
      v-model="refValue"
      v-on-click-outside="handleClickOutside"
      :options="refOptions"
      :multiple="true"
      :reduce="(option: any) => option[reduceKey]"
      label="text"
      :deselect-from-dropdown="true"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :dropdown-should-open="() => refOpen"
      :searchable="true"
      :autoscroll="false"
      :class="[mode]"
      @keydown.esc="handleClickOutside"
      @search="handleSearch"
      @search:focus="handleFocus"
      @focus="handleFocus"
      @search:blur="handleBlur"
      @option:selecting="handleBeforeSelect"
      @option:deselecting="handleBeforeDeselect"
      @option:selected="handleSelected"
      @option:deselected="handleDeselected"
      @open="handleOpen"
    >
      <template
        v-if="isAllAvailable"
        #list-header
      >
        <li
          class="vs__dropdown-option"
          role="option"
          @click="setToggleAll"
        >
          <div class="list-option">
            <div :class="['list-option__checkbox', { 'checked': refAllSelected }]">
              <FontAwesomeIcon
                :icon="isChecked(refAllSelected)"
              />
            </div>
            <AppText
              name="body2"
              class="list-option__info"
            >
              All
            </AppText>
          </div>
        </li>
      </template>
      <template #open-indicator="{ attributes }">
        <span
          v-show="!refOpen"
          :class="['input-select__arrow', { 'disabled': isDisabled }]"
          v-bind="attributes"
        >
          <FontAwesomeIcon :icon="faChevronDown" />
        </span>
        <span
          v-show="refOpen"
          :class="['input-select__close', { 'disabled': isDisabled }]"
          v-bind="attributes"
          @click="handleClickClose"
        >
          <FontAwesomeIcon :icon="faXmark" />
        </span>
      </template>
      <template #selected-option-container="{ option }">
        {{ option.title }}
      </template>
      <template #search="{ attributes, events }">
        <input
          :class="['vs__search', placeholderClass, 'truncate']"
          v-bind="attributes"
          :placeholder="inputText"
          v-on="events"
        >
      </template>
      <template #option="option">
        <div
          class="list-option"
          :data-option="option.id"
        >
          <div :class="['list-option__checkbox', { 'checked': option.selected }]">
            <FontAwesomeIcon
              :icon="isChecked(option.selected)"
            />
          </div>
          <AppText
            name="body2"
            :class="['list-option__info', option.color || '', option.infoClass || '']"
          >
            <span>
              {{ option.text }}
            </span>
            <div
              v-if="option.label"
              :class="['list-option__label', option.labelColor || '']"
            >
              {{ option.label }}
            </div>
          </AppText>
        </div>
      </template>
    </VueSelect>
  </div>
</template>

<style lang="scss" scoped src="../styles/_multi-select-searchable.scss" />
